import React, { useEffect, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'i18n';
import { makeStyles } from 'tss-react/mui';
import { Market } from 'api/general/models';
import DetroitMarketSponsor from 'components/svg/DetroitMarketSponsor.svg';
import MiamiPresentedBy from 'components/svg/MiamiPresentedBy.svg';
import Image from 'components/common/Image';

interface IProps {
  markets: Market[];
  market: string;
  onSetMarket: (market: string) => void;
}

const useStyles = makeStyles()((theme) => ({
  titleText: {
    textAlign: 'left',
    fontWeight: 700,
    fontSize: 42,
    lineHeight: '120%',
    letterSpacing: '-0.02em',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up('lg')]: {
      maxWidth: 500,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 24,
      marginBottom: theme.spacing(2),
    },
  },
  market: {
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    color: theme.palette.primary.main,
    padding: 0,
    fontWeight: 700,
    fontSize: 42,
    lineHeight: '120%',
    background: 'none',
    border: 'none',
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: 24,
    },
  },
  presentedByMobile: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '0 !important',
  },
  MiamiPresentedBy: {
    marginTop: -24,
    [theme.breakpoints.down('sm')]: {
      marginTop: -12,
    },
  },
  northernVirginiaPresentedBy: {
    height: 60,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: -12,
  },
  northernVirginiaLogo: {
    width: 120,
    height: 60,
  },
  pozezJCCLogo: {
    width: 90,
    height: 45,
  },
}));

const HomepageBannerTitle: React.FC<React.PropsWithChildren<IProps>> = (props) => {
  const { markets, market, onSetMarket } = props;
  const { classes } = useStyles();
  const [t] = useTranslation('public');
  const [anchorEl, setAnchorEl] = useState(null);
  const selectMarket = markets.find(({ slug }) => market === slug);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const handleOpenClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (slug: string) => () => {
    handleClose();
    onSetMarket(slug);
  };

  const marketComparator = (a: any, b: any) => {
    const marketsOrder = {
      'ann-arbor': 1,
      cincinnati: 2,
      detroit: 3,
      miami: 4,
      montreal: 5,
      'northern-virginia': 6,
      'silicon-valley': 7,
      'test-cincinnati': 8,
      toronto: 9,
      usa: 10,
      canada: 11,
      'north-america': 12,
      international: 13,
    };
    return marketsOrder[a.slug as keyof typeof marketsOrder] - marketsOrder[b.slug as keyof typeof marketsOrder];
  };

  return (
    <>
      <Typography className={classes.titleText} variant="h3">
        {t<string>('screen.homepage.title')}{' '}
        {mounted && (
          <Typography className={classes.market} onClick={handleOpenClick} component="span">
            {selectMarket?.title}
            <IconButton size="small">
              <ArrowDropDownIcon color="primary" />
            </IconButton>
          </Typography>
        )}
      </Typography>
      <Menu
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {markets
          .sort((a: any, b: any) => marketComparator(a, b))
          .map((market) => (
            <MenuItem
              key={market.slug}
              onClick={handleClick(market.slug)}
              divider={
                market.slug === 'test-cincinnati' || market.slug === 'north-america' || market.slug === 'toronto'
              }
            >
              {market.title}
            </MenuItem>
          ))}
      </Menu>
      {market === 'detroit' && <DetroitMarketSponsor style={{ marginTop: -24 }} />}
      {market === 'miami' && <MiamiPresentedBy className={classes.MiamiPresentedBy} />}
      {market === 'northern-virginia' && (
        <Box className={classes.northernVirginiaPresentedBy}>
          <Typography variant="body1" fontWeight={600}>
            {t<string>('sponsorsBlock.presentedBy')}
          </Typography>

          <Image
            variant="aspectRatio"
            objectFit="contain"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            className={classes.pozezJCCLogo}
            src="/static/img/logo/PozezJCCLogo.png"
            alt="/static/img/logo/PozezJCCLogo.png"
          />
          <Image
            variant="aspectRatio"
            objectFit="contain"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            className={classes.northernVirginiaLogo}
            src="/static/img/logo/JFGWLogo.png"
            alt="/static/img/logo/JFGWLogo.png"
          />
        </Box>
      )}
    </>
  );
};

export default HomepageBannerTitle;
